import { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { ImportMappingGrid } from '@/integration/ImportMappingGrid';
import type { MappingTargetField } from '@/integration/importMappingCells';
import { toMappingTargetFields } from '@/integration/importMappingFields';
import { ImportWarningAlert } from './ImportWarningAlert';
import type { OnlineImportTarget } from './onlineImportService';
import {
  fileColumns,
  noKeyMapped,
  unmappedRequiredFields,
  type OnlineImportEvent,
  type OnlineImportState,
} from './onlineImportState';

interface OnlineImportColumnsProps {
  state: OnlineImportState;
  target: OnlineImportTarget;
  dispatch: (event: OnlineImportEvent) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Which column fills which field, for this run: the profile's mapping, or the server's suggestion
 * when there is no profile. What the reader could not decide, which fields a new record still
 * needs, and that no column identifies an existing record (every row would then create one) are
 * said whether the columns are open or not.
 */
export function OnlineImportColumns({ state, target, dispatch, open, onOpenChange }: OnlineImportColumnsProps) {
  const { t } = useTranslation();
  const analysis = state.analysis!;
  const fields = useMemo<MappingTargetField[]>(() => toMappingTargetFields(target.fields), [target.fields]);
  const missing = unmappedRequiredFields(target, state.mappings);
  const keyLabels = target.keyFields.map((key) => target.fields.find((f) => f.name === key)?.label ?? key);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {analysis.exceedsMaxRows && (
        <Alert severity="error">
          {t('onlineImport.tooManyRows', { defaultValue: 'The file has more than {{count}} rows. Import it in parts, or through an integration job.', count: target.maxRows })}
        </Alert>
      )}
      {analysis.warnings.map((warning) => <ImportWarningAlert key={`${warning.code}-${warning.column}`} warning={warning} />)}
      {noKeyMapped(target, state.mappings) && (
        <Alert severity="warning">
          {t('onlineImport.noKey', { defaultValue: 'No column is mapped to {{keys}}, so every row creates a new record.', keys: keyLabels.join(', ') })}
        </Alert>
      )}
      {missing.length > 0 && (
        <Alert severity="info">
          {t('onlineImport.missingRequired', { defaultValue: 'A new record needs {{fields}}; rows without them can only update.', fields: missing.join(', ') })}
        </Alert>
      )}

      <Accordion
        disableGutters
        variant="outlined"
        expanded={open}
        onChange={(_event, expanded) => onOpenChange(expanded)}
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{ '&::before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
          <Typography variant="body2">
            {state.preset
              ? t('onlineImport.columns.adjust', { defaultValue: 'Adjust columns for this run' })
              : t('onlineImport.columns.title', { defaultValue: 'Columns' })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, pt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <ImportMappingGrid
            rows={state.mappings}
            onRowsChange={(mappings) => dispatch({ type: 'mappingsChanged', mappings })}
            onAdd={() => dispatch({
              type: 'mappingsChanged',
              mappings: [...state.mappings, { id: `new-${Date.now()}`, sourceColumn: '', targetField: '', transformChain: '', defaultValue: '' }],
            })}
            sourceColumns={fileColumns(analysis)}
            fields={fields}
          />
          {state.preset && (
            <Typography variant="caption" color="text.secondary">
              {t('onlineImport.columns.notSaved', { defaultValue: 'Changes here are not saved to the profile.' })}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
